import React, { useCallback, useEffect, useState } from 'react';
import {
    Button, DatePicker, Descriptions, Drawer, Dropdown, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Timeline, Typography, message
} from 'antd';
import { DeleteOutlined, DownloadOutlined, EditOutlined, EyeOutlined, FileExcelOutlined, FilePdfOutlined, FileWordOutlined, ReloadOutlined } from '@ant-design/icons';
import {
    api, ActStatusTag, asObj, AUDIT_ACTIONS, coefText, downloadAct, errorText, formatChange, hasRole, money, PAYMENT_COLORS, PAYMENT_TITLES, qty, ruDate, ruDateTime, showError, useSub
} from './shared';

const { Text } = Typography;

const TRANSITION_LABELS = {
    'draft>review': 'Отправить на проверку',
    'review>checked': 'Проверено СДО',
    'review>draft': 'Вернуть в черновик',
    'checked>approved': 'Утвердить',
    'checked>draft': 'Вернуть в черновик',
    'approved>accounting': 'Передать в бухгалтерию',
    'approved>checked': 'Вернуть на проверку',
    'accounting>signed': 'Подписан',
    'accounting>approved': 'Вернуть',
    'signed>closed': 'Закрыть'
};
const BACK = new Set(['review>draft', 'checked>draft', 'approved>checked', 'accounting>approved']);

const ActDrawer = ({ actId, onClose, onChanged }) => {
    const { me, meta } = useSub();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        if (!actId) return;
        setLoading(true);
        try {
            const { data: d } = await api.get(`/acts/${actId}`);
            setData(d);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }, [actId]);

    useEffect(() => {
        setData(null);
        load();
    }, [load]);

    const act = data?.act;
    const editable = act?.status === 'draft' && hasRole(me, 'sdo');
    const run = async (fn, ok) => {
        try {
            await fn();
            if (ok) message.success(ok);
            await load();
            onChanged?.();
        } catch (error) {
            Modal.error({ title: 'Ошибка', content: errorText(error) });
        }
    };

    const transitions = act ? Object.entries(meta.transitions?.[act.status] || {}).filter(([, roles]) => hasRole(me, ...roles)) : [];
    const changeStatus = to => {
        const key = `${act.status}>${to}`;
        let comment = '';
        Modal.confirm({
            title: `${TRANSITION_LABELS[key] || to}?`,
            content: (
                <div>
                    <div style={{ marginBottom: 8 }}>Акт №{act.number}: «{meta.act_statuses?.[act.status]}» → «{meta.act_statuses?.[to]}»</div>
                    <Input.TextArea rows={2} placeholder="Комментарий (необязательно)" onChange={e => { comment = e.target.value; }} />
                </div>
            ),
            okText: 'Подтвердить',
            cancelText: 'Отмена',
            onOk: () => run(() => api.post(`/acts/${act.id}/status`, { to, comment }), 'Статус изменён')
        });
    };

    const items = act?.items || [];
    const columns = [
        { title: '№', width: 45, render: (_, __, i) => i + 1 },
        { title: 'Объект', dataIndex: 'object_number', width: 100 },
        { title: 'Заявка', dataIndex: 'request_number', width: 130 },
        { title: 'Дата', dataIndex: 'done_date', width: 90, render: ruDate },
        { title: 'Наименование', dataIndex: 'work_name', ellipsis: { showTitle: true } },
        { title: 'Исполнитель', dataIndex: 'executor', width: 150 },
        { title: 'Кол-во', dataIndex: 'quantity', width: 70, align: 'right', render: qty },
        { title: 'Цена заказч.', dataIndex: 'customer_price', width: 100, align: 'right', render: money },
        { title: 'Цена', dataIndex: 'price', width: 95, align: 'right', render: money },
        { title: 'Сумма без НДС', dataIndex: 'amount_no_vat', width: 110, align: 'right', render: money },
        { title: 'НДС', dataIndex: 'vat_amount', width: 90, align: 'right', render: money },
        { title: 'Сумма с НДС', dataIndex: 'amount_with_vat', width: 110, align: 'right', render: money },
        ...(editable ? [{
            title: '',
            width: 70,
            render: (_, r) => (
                <Space size={0}>
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setEditItem(r); form.setFieldsValue({ quantity: Number(r.quantity), price: Number(r.price), comment: '' }); }} />
                    <Popconfirm title="Убрать работу из акта? Она снова станет доступной." onConfirm={() => run(() => api.delete(`/acts/${act.id}/items/${r.id}`), 'Строка удалена')}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }] : [])
    ];

    return (
        <Drawer
            open={!!actId}
            onClose={onClose}
            width="92%"
            title={act ? <Space>Акт №{act.number}<ActStatusTag status={act.status} meta={meta} />{act.corrects_act_id && <Tag color="orange">корректировка</Tag>}</Space> : 'Акт'}
            loading={loading && !act}
            extra={act && (
                <Space wrap>
                    <Dropdown
                        menu={{
                            items: [
                                { key: 'docx', label: 'Word — новый (объект → заявка)' },
                                { key: 'docx-old', label: 'Word — старый (плоская таблица)' }
                            ],
                            onClick: ({ key }) => downloadAct(act, key)
                        }}
                    >
                        <Button type="primary" icon={<FileWordOutlined />}>Word</Button>
                    </Dropdown>
                    <Button icon={<EyeOutlined />} onClick={() => downloadAct(act, 'pdf-view')}>Просмотр</Button>
                    <Button icon={<FileExcelOutlined />} onClick={() => downloadAct(act, 'xlsx')}>Excel</Button>
                    <Button icon={<FilePdfOutlined />} onClick={() => downloadAct(act, 'pdf')}>PDF</Button>
                </Space>
            )}
        >
            {act && (
                <>
                    <Descriptions size="small" bordered column={{ xs: 1, md: 2, xl: 4 }} style={{ marginBottom: 12 }}>
                        <Descriptions.Item label="Дата акта">{ruDate(act.act_date)}</Descriptions.Item>
                        <Descriptions.Item label="Период">{ruDate(act.period_start)} — {ruDate(act.period_end)}</Descriptions.Item>
                        <Descriptions.Item label="Город">{act.city}</Descriptions.Item>
                        <Descriptions.Item label="Регион">{act.region}</Descriptions.Item>
                        <Descriptions.Item label="Подрядчик">{act.contractor_name}</Descriptions.Item>
                        <Descriptions.Item label="Субподрядчик">{act.subcontractor?.full_name}</Descriptions.Item>
                        <Descriptions.Item label="Договор">
                            {[act.subcontractor?.contract_name, act.subcontractor?.contract_number && `№${act.subcontractor.contract_number}`,
                                act.subcontractor?.contract_date && `от ${ruDate(act.subcontractor.contract_date)}`].filter(Boolean).join(' ') || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Заказчик">{act.customer}</Descriptions.Item>
                        <Descriptions.Item label="Коэффициент">{coefText(act.coefficient)}</Descriptions.Item>
                        <Descriptions.Item label="НДС">{act.vat_mode === 'with' ? `${Number(act.vat_rate)}%` : 'без НДС'}</Descriptions.Item>
                        <Descriptions.Item label="Работ">{act.works_count}</Descriptions.Item>
                        <Descriptions.Item label="Сумма заказчика">{money(act.base_amount)}</Descriptions.Item>
                        <Descriptions.Item label="Сумма без НДС"><b>{money(act.amount_no_vat)}</b></Descriptions.Item>
                        <Descriptions.Item label="НДС">{money(act.vat_amount)}</Descriptions.Item>
                        <Descriptions.Item label="Сумма с НДС"><b>{money(act.amount_with_vat)}</b></Descriptions.Item>
                        <Descriptions.Item label="Оплата">
                            {hasRole(me, 'accountant') ? (
                                <Select
                                    size="small"
                                    value={act.payment_status}
                                    style={{ width: 140 }}
                                    onChange={v => run(() => api.post(`/acts/${act.id}/payment`, { payment_status: v }), 'Статус оплаты сохранён')}
                                    options={Object.entries(PAYMENT_TITLES).map(([value, label]) => ({ value, label }))}
                                />
                            ) : <Tag color={PAYMENT_COLORS[act.payment_status]}>{PAYMENT_TITLES[act.payment_status]}</Tag>}
                        </Descriptions.Item>
                    </Descriptions>

                    <Space wrap style={{ marginBottom: 12 }}>
                        {transitions.map(([to]) => {
                            const key = `${act.status}>${to}`;
                            return (
                                <Button key={to} type={BACK.has(key) ? 'default' : 'primary'} danger={BACK.has(key)} onClick={() => changeStatus(to)}>
                                    {TRANSITION_LABELS[key] || to}
                                </Button>
                            );
                        })}
                        {editable && (
                            <Popconfirm title="Удалить черновик? Работы снова станут доступны." onConfirm={() => run(async () => { await api.delete(`/acts/${act.id}`); onClose(); }, 'Черновик удалён')}>
                                <Button danger icon={<DeleteOutlined />}>Удалить черновик</Button>
                            </Popconfirm>
                        )}
                        {['signed', 'closed'].includes(act.status) && hasRole(me, 'sdo') && (
                            <Button
                                onClick={() => {
                                    let comment = '';
                                    Modal.confirm({
                                        title: 'Создать корректировочный документ?',
                                        content: <Input.TextArea rows={2} placeholder="Причина корректировки" onChange={e => { comment = e.target.value; }} />,
                                        okText: 'Создать',
                                        onOk: () => run(() => api.post(`/acts/${act.id}/correction`, { comment }), 'Корректировка создана — найдите её в списке актов')
                                    });
                                }}
                            >
                                Создать корректировку
                            </Button>
                        )}
                        {act.status === 'closed' && <Text type="secondary">Акт закрыт — изменения только через корректировку.</Text>}
                        {data.corrections?.length > 0 && (
                            <Text type="secondary">Корректировки: {data.corrections.map(c => c.number).join(', ')}</Text>
                        )}
                    </Space>

                    <Table
                        size="small"
                        rowKey="id"
                        dataSource={items}
                        columns={columns}
                        pagination={items.length > 200 ? { pageSize: 200 } : false}
                        scroll={{ x: 1500 }}
                        summary={() => (
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={9}><b>ИТОГО</b></Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right"><b>{money(act.amount_no_vat)}</b></Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right"><b>{money(act.vat_amount)}</b></Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align="right"><b>{money(act.amount_with_vat)}</b></Table.Summary.Cell>
                                {editable && <Table.Summary.Cell index={4} />}
                            </Table.Summary.Row>
                        )}
                    />

                    <Typography.Title level={5} style={{ marginTop: 16 }}>История</Typography.Title>
                    <Timeline
                        items={(data.history || []).map(h => ({
                            children: (
                                <div>
                                    <Text type="secondary">{ruDateTime(h.created_at || h.createdAt)}</Text> · <b>{h.user_name || 'система'}</b> · {AUDIT_ACTIONS[h.action] || h.action}
                                    {h.action === 'status' ? (
                                        <> : {meta.act_statuses?.[asObj(h.before).status]} → {meta.act_statuses?.[asObj(h.after).status]}</>
                                    ) : (
                                        <>{h.before && <> · было: {formatChange(h.before)}</>}{h.after && <> · стало: {formatChange(h.after)}</>}</>
                                    )}
                                    {h.comment && <div><Text italic>{h.comment}</Text></div>}
                                </div>
                            )
                        }))}
                    />

                    <Modal
                        open={!!editItem}
                        title="Изменить строку"
                        okText="Сохранить"
                        onCancel={() => setEditItem(null)}
                        onOk={() => form.validateFields().then(v => run(async () => {
                            await api.patch(`/acts/${act.id}/items/${editItem.id}`, v);
                            setEditItem(null);
                        }, 'Строка изменена'))}
                    >
                        <Text type="secondary">{editItem?.work_name}</Text>
                        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
                            <Space>
                                <Form.Item name="quantity" label="Количество" rules={[{ required: true }]}><InputNumber min={0.001} step={1} /></Form.Item>
                                <Form.Item name="price" label="Цена субподряда" rules={[{ required: true }]}><InputNumber min={0.01} step={1} /></Form.Item>
                            </Space>
                            <Form.Item name="comment" label="Причина изменения" rules={[{ required: true, message: 'Укажите причину' }]}><Input /></Form.Item>
                        </Form>
                    </Modal>
                </>
            )}
        </Drawer>
    );
};

const ActsTab = ({ openActId, onOpenHandled }) => {
    const { meta, subcontractors } = useSub();
    const [period, setPeriod] = useState(null);
    const [status, setStatus] = useState(null);
    const [subcontractorId, setSubcontractorId] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [current, setCurrent] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/acts', {
                params: { period: period ? period.format('YYYY-MM') : undefined, status: status || undefined, subcontractorId: subcontractorId || undefined }
            });
            setRows(data);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }, [period, status, subcontractorId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (openActId) {
            setCurrent(openActId);
            onOpenHandled?.();
        }
    }, [openActId, onOpenHandled]);

    const total = f => rows.reduce((s, r) => s + Number(r[f] || 0), 0);

    return (
        <>
            <Space style={{ marginBottom: 12 }} wrap>
                <DatePicker picker="month" placeholder="Все периоды" value={period} onChange={setPeriod} format="MMMM YYYY" />
                <Select allowClear placeholder="Все статусы" style={{ width: 220 }} value={status} onChange={setStatus}
                    options={Object.entries(meta.act_statuses || {}).map(([value, label]) => ({ value, label }))} />
                <Select allowClear showSearch optionFilterProp="label" placeholder="Все субподрядчики" style={{ width: 220 }} value={subcontractorId} onChange={setSubcontractorId}
                    options={subcontractors.map(x => ({ value: x.id, label: x.short_name || x.full_name }))} />
                <Button icon={<ReloadOutlined />} onClick={load}>Обновить</Button>
                <Text type="secondary">Актов: {rows.length} · без НДС {money(total('amount_no_vat'))} · с НДС {money(total('amount_with_vat'))}</Text>
            </Space>
            <Table
                size="small"
                rowKey="id"
                loading={loading}
                dataSource={rows}
                pagination={{ pageSize: 50 }}
                scroll={{ x: 1500 }}
                onRow={r => ({ onDoubleClick: () => setCurrent(r.id) })}
                columns={[
                    { title: 'Номер', dataIndex: 'number', width: 190, render: (v, r) => <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setCurrent(r.id)}>{v}</Button> },
                    { title: 'Дата', dataIndex: 'act_date', width: 95, render: ruDate },
                    { title: 'Период', dataIndex: 'period', width: 85 },
                    { title: 'Субподрядчик', render: (_, r) => r.subcontractor?.short_name || r.subcontractor?.full_name },
                    { title: 'Регион', dataIndex: 'region', width: 150 },
                    { title: 'Работ', dataIndex: 'works_count', width: 70, align: 'right' },
                    { title: 'К', dataIndex: 'coefficient', width: 100, ellipsis: true, render: v => <span title={coefText(v)}>{coefText(v)}</span> },
                    { title: 'Без НДС', dataIndex: 'amount_no_vat', width: 120, align: 'right', render: money },
                    { title: 'НДС', dataIndex: 'vat_amount', width: 100, align: 'right', render: money },
                    { title: 'С НДС', dataIndex: 'amount_with_vat', width: 120, align: 'right', render: money },
                    { title: 'Статус', dataIndex: 'status', width: 190, render: (v, r) => <Space size={2}><ActStatusTag status={v} meta={meta} />{r.corrects_act_id && <Tag color="orange">корр.</Tag>}</Space> },
                    { title: 'Оплата', dataIndex: 'payment_status', width: 105, render: v => <Tag color={PAYMENT_COLORS[v]}>{PAYMENT_TITLES[v]}</Tag> },
                    {
                        title: '',
                        width: 90,
                        render: (_, r) => (
                            <Space size={0}>
                                <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => setCurrent(r.id)} />
                                <Dropdown menu={{
                                    items: [
                                        { key: 'docx', icon: <FileWordOutlined />, label: 'Word — новый' },
                                        { key: 'docx-old', icon: <FileWordOutlined />, label: 'Word — старый' },
                                        { key: 'xlsx', icon: <FileExcelOutlined />, label: 'Excel' },
                                        { key: 'pdf', icon: <FilePdfOutlined />, label: 'PDF' }
                                    ],
                                    onClick: ({ key }) => downloadAct(r, key)
                                }}>
                                    <Button size="small" type="text" icon={<DownloadOutlined />} />
                                </Dropdown>
                            </Space>
                        )
                    }
                ]}
            />
            <ActDrawer actId={current} onClose={() => setCurrent(null)} onChanged={load} />
        </>
    );
};

export default ActsTab;
